// src/index.ts
import { z as z4 } from "zod";

// src/erp.ts
import { z } from "zod";
var erpMarketplaceSchema = z.enum(["ozon", "wb"]);
var erpAccessSchema = z.enum(["viewer", "editor"]);
var erpProductStatusSchema = z.enum(["draft", "ready", "listed", "archived"]);
var label = z.string().trim().min(1).max(160);
var optionalText = (max) => z.string().trim().max(max).default("");
var webUrl = z.union([z.literal(""), z.url({ protocol: /^https?$/ }).max(2e3)]).default("");
var erpCreateStoreSchema = z.object({
  name: label,
  marketplace: erpMarketplaceSchema,
  externalId: optionalText(120)
});
var erpUpdateStoreSchema = erpCreateStoreSchema.extend({
  expectedVersion: z.number().int().positive()
});
var erpMemberSchema = z.object({ userId: z.uuid(), access: erpAccessSchema });
var erpCreateProductSchema = z.object({
  storeId: z.uuid(),
  sku: z.string().trim().min(1).max(100),
  name: label,
  status: erpProductStatusSchema.default("draft"),
  category: optionalText(160),
  specification: optionalText(1e3),
  material: optionalText(300),
  dimensions: optionalText(300),
  supplierUrl: webUrl,
  sourceUrl: webUrl,
  notes: optionalText(5e3)
});
var erpUpdateProductSchema = erpCreateProductSchema.omit({ storeId: true }).extend({
  expectedVersion: z.number().int().positive()
});
var erpProductQuerySchema = z.object({
  storeId: z.uuid().optional(),
  search: z.string().trim().max(160).default(""),
  status: erpProductStatusSchema.optional(),
  page: z.coerce.number().int().min(1).max(1e6).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  ownership: z.enum(["mine", "all", "unassigned"]).optional(),
  saleScope: z.enum(["on_sale", "all"]).optional(),
  ownerId: z.uuid().optional(),
  skus: z.array(z.string().trim().min(1).max(100)).max(100).optional()
});
var batchItems = z.array(z.object({ id: z.uuid(), expectedVersion: z.number().int().positive() })).min(1).max(100).refine((items) => new Set(items.map((i) => i.id)).size === items.length, "\u4E0D\u80FD\u91CD\u590D\u9009\u62E9\u5546\u54C1");
var erpProductBatchSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("set_status"), items: batchItems, status: erpProductStatusSchema }),
  z.object({ action: z.literal("assign_owner"), items: batchItems, ownerId: z.uuid().nullable() }),
  z.object({ action: z.literal("claim"), items: batchItems })
]);

// src/erp-workflow.ts
import { z as z2 } from "zod";
var text = (max = 2e3) => z2.string().trim().max(max).default("");
var money = z2.number().finite().min(0).max(1e8).nullable().default(null);
var rate = z2.number().finite().min(0).max(0.99).nullable().default(null);
var url = z2.union([z2.literal(""), z2.url({ protocol: /^https?$/ }).max(2e3)]).default("");
var date = text(10).refine((v) => {
  if (!v) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const time = Date.parse(`${v}T00:00:00Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === v;
}, "\u586B\u5199\u6709\u6548\u65E5\u671F YYYY-MM-DD");
var erpVariantSchema = z2.object({
  id: z2.uuid(),
  sku: z2.string().trim().min(1).max(100),
  specification: text(1e3),
  supplierName: text(160),
  supplierUrl: url,
  supplierSpecification: text(1e3),
  purchaseCny: money,
  sellingRub: money,
  logisticsCny: money,
  logisticsSource: text(500),
  packagedWeightKg: money,
  lengthCm: money,
  widthCm: money,
  heightCm: money,
  packagingSource: text(500)
});
var erpPricingSchema = z2.object({
  rubPerCny: z2.number().finite().positive().max(1e6).nullable().default(null),
  exchangeSource: text(300),
  rateDate: date,
  fulfillment: text(100),
  category: text(160),
  commissionSource: text(500),
  effectiveDate: date,
  commissionRate: rate,
  serviceFeeRate: rate,
  advertisingRate: rate,
  returnLossRate: rate,
  taxRate: rate,
  exchangeMarkupRate: rate,
  targetMarginRate: rate,
  otherCny: money
});
var erpContentSchema = z2.object({
  titleRu: text(500),
  descriptionRu: text(15e3),
  sellingPoints: z2.array(
    z2.object({
      text: z2.string().trim().min(1).max(1e3),
      evidence: z2.string().trim().min(1).max(2e3)
    })
  ).max(20).default([]),
  imagePlan: text(1e4),
  gaps: text(5e3)
});
var erpWorkflowSchema = z2.object({
  researchUrl: url,
  sourceText: text(3e4),
  variants: z2.array(erpVariantSchema).max(100).default([]),
  pricing: erpPricingSchema.default(() => erpPricingSchema.parse({})),
  content: erpContentSchema.default(() => erpContentSchema.parse({})),
  listingSku: text(100),
  listingUrl: url
}).superRefine((v, ctx) => {
  for (const key of ["id", "sku"]) {
    if (new Set(v.variants.map((x) => x[key])).size !== v.variants.length)
      ctx.addIssue({
        code: "custom",
        path: ["variants"],
        message: `\u89C4\u683C${key === "sku" ? "\u8D27\u53F7" : "\u6807\u8BC6"}\u4E0D\u80FD\u91CD\u590D`
      });
  }
});
var erpSaveWorkflowSchema = z2.object({
  expectedVersion: z2.number().int().min(0),
  expectedProductVersion: z2.number().int().positive(),
  value: erpWorkflowSchema
});
var erpGenerateContentSchema = z2.object({
  expectedVersion: z2.number().int().positive(),
  expectedProductVersion: z2.number().int().positive()
});

// src/erp-operations.ts
import { z as z3 } from "zod";
var erpDailyTaskSchema = z3.enum(["orders", "questions", "selection", "listing"]);
var erpSourceRowSchema = z3.object({
  file: z3.string().min(1).max(260),
  sheet: z3.string().min(1).max(100),
  row: z3.number().int().positive(),
  sku: z3.string().trim().min(1).max(100),
  name: z3.string().max(160).default(""),
  category: z3.string().max(160).default(""),
  specification: z3.string().max(1e3).default(""),
  supplierUrl: z3.string().max(2e3).default(""),
  purchasePrice: z3.string().max(160).default(""),
  purchasePriceLabel: z3.string().max(100).default(""),
  skuCell: z3.string().max(30).default(""),
  sourceEmployee: z3.string().max(160).default(""),
  storeName: z3.string().max(160).default(""),
  platformSku: z3.string().max(100).default(""),
  archived: z3.boolean().default(false)
});

// src/index.ts
var platformSchema = z4.enum(["wb"]);
var analysisModeSchema = z4.enum(["standalone", "wb"]);
var userRoleSchema = z4.enum(["admin", "user"]);
var userDecisionSchema = z4.enum(["unmarked", "keep", "discard"]);
function platformsForAnalysisMode(mode) {
  return mode === "standalone" ? [] : [mode];
}
var loginRequestSchema = z4.object({
  username: z4.string().trim().min(2).max(64),
  password: z4.string().min(6).max(256),
  rememberMe: z4.boolean().default(true)
});
var registerRequestSchema = z4.object({
  username: z4.string().trim().min(2).max(64),
  password: z4.string().min(6).max(256)
});
var publicUserSchema = z4.object({
  id: z4.string().uuid(),
  username: z4.string(),
  role: userRoleSchema,
  isActive: z4.boolean(),
  version: z4.number().int().positive()
});
var createUserRequestSchema = z4.object({
  username: z4.string().trim().min(2).max(64),
  password: z4.string().min(6).max(256),
  role: userRoleSchema
});
var updateUserRequestSchema = z4.object({
  role: userRoleSchema.optional(),
  isActive: z4.boolean().optional(),
  expectedVersion: z4.number().int().positive()
}).refine((value) => value.role !== void 0 || value.isActive !== void 0, {
  message: "At least one field must be updated."
});
var resetPasswordRequestSchema = z4.object({
  password: z4.string().min(6).max(256),
  expectedVersion: z4.number().int().positive()
});
var keywordSearchRequestSchema = z4.object({
  query: z4.object({
    text: z4.string().max(300).default(""),
    mode: z4.enum(["exact", "all_words", "any_word"]).default("all_words"),
    exclude: z4.array(z4.string().max(100)).max(20).default([]),
    scope: z4.enum(["current_platform", "all_platforms"]).default("current_platform")
  }).default({ text: "", mode: "all_words", exclude: [], scope: "current_platform" }),
  filters: z4.object({
    decisions: z4.array(userDecisionSchema).default(["unmarked", "keep"]),
    includeHighRisk: z4.boolean().default(false),
    includeDiscarded: z4.boolean().default(false),
    score: z4.object({
      gte: z4.number().min(0).max(100).optional(),
      lte: z4.number().min(0).max(100).optional()
    }).optional(),
    supplyDemandRatio: z4.object({ gte: z4.number().min(0).optional(), lte: z4.number().min(0).optional() }).optional()
  }).default({ decisions: ["unmarked", "keep"], includeHighRisk: false, includeDiscarded: false }),
  sort: z4.array(
    z4.object({
      field: z4.enum([
        "updatedAt",
        "originalKeyword",
        "currentDemand",
        "supplyDemandRatio",
        "compositeScore"
      ]),
      direction: z4.enum(["asc", "desc"])
    })
  ).max(3).default([{ field: "updatedAt", direction: "desc" }]),
  cursor: z4.string().max(512).nullable().default(null),
  limit: z4.number().int().min(1).max(200).default(50)
});
var updateDecisionRequestSchema = z4.object({
  decision: userDecisionSchema,
  expectedVersion: z4.number().int().positive()
});
var batchUpdateDecisionRequestSchema = z4.object({
  decision: userDecisionSchema,
  records: z4.array(z4.object({ id: z4.string().uuid(), expectedVersion: z4.number().int().positive() })).min(1).max(1e3)
});
var createUploadRequestSchema = z4.object({
  platform: platformSchema,
  fileName: z4.string().min(1).max(255),
  fileSize: z4.number().int().positive(),
  mimeType: z4.string().min(1).max(160),
  checksum: z4.string().regex(/^[a-fA-F0-9]{64}$/),
  chunkSize: z4.number().int().min(64 * 1024).max(32 * 1024 * 1024)
});
var preferenceValueSchema = z4.record(z4.string(), z4.unknown());
var createDictionaryEntrySchema = z4.object({
  canonicalKeyword: z4.string().trim().min(1).max(200),
  chineseTranslation: z4.string().trim().min(1).max(200).nullable().default(null),
  standardProductName: z4.string().trim().min(1).max(200).nullable().default(null),
  productType: z4.string().trim().min(1).max(120).nullable().default(null),
  mainKeyword: z4.string().trim().min(1).max(200).nullable().default(null),
  aliases: z4.array(
    z4.object({
      value: z4.string().trim().min(1).max(200),
      kind: z4.enum(["synonym", "grammar_variant", "related"])
    })
  ).max(100).default([]),
  locked: z4.boolean().default(true)
});
var createPromptVersionSchema = z4.object({
  systemPrompt: z4.string().trim().min(20).max(2e4),
  userTemplate: z4.string().trim().min(5).max(2e4),
  responseSchema: z4.record(z4.string(), z4.unknown())
});
var keywordProcessingActionSchema = z4.object({
  action: z4.enum(["ai_review", "recalculate_score", "rerun_risk", "refresh_dictionary"]),
  records: z4.array(z4.string().uuid()).min(1).max(1e3)
});
var competitorRefreshSchema = z4.object({
  links: z4.array(z4.string().url()).max(10).default([]),
  force: z4.boolean().default(false)
});
var competitorSampleUpdateSchema = z4.object({
  selected: z4.boolean().optional(),
  locked: z4.boolean().optional(),
  expectedVersion: z4.number().int().positive()
});
var productMaterialSchema = z4.object({
  rawText: z4.string().trim().min(1).max(1e5),
  sourceLink: z4.string().url().nullable().default(null)
});
var updateExtractedAttributeSchema = z4.object({
  value: z4.string().trim().min(1).max(500),
  expectedVersion: z4.number().int().positive()
});
var keywordRelationsSchema = z4.object({
  relations: z4.array(
    z4.object({
      keywordText: z4.string().trim().min(1).max(200),
      chineseTranslation: z4.string().trim().min(1).max(200).nullable(),
      usageKind: z4.enum(["title", "description", "attribute", "unrelated", "risk", "duplicate"]),
      selected: z4.boolean(),
      relevance: z4.number().min(0).max(1).nullable()
    })
  ).max(100)
});
var productCardDraftSchema = z4.object({
  productName: z4.string().trim().min(1).max(200),
  materialId: z4.string().uuid().nullable().default(null),
  titleKeywords: z4.array(z4.string().trim().min(1).max(200)).max(20).default([]),
  descriptionKeywords: z4.array(z4.string().trim().min(1).max(200)).max(50).default([]),
  category: z4.string().trim().min(1).max(200).nullable().default(null),
  candidates: z4.number().int().min(1).max(4).default(1)
});
var saveProductCardSchema = z4.object({
  title: z4.string().trim().min(1).max(200),
  description: z4.string().trim().min(1).max(2e4),
  attributes: z4.record(z4.string(), z4.string()),
  keywords: z4.array(z4.string().trim().min(1).max(200)).max(100),
  category: z4.string().trim().min(1).max(200).nullable(),
  sourceStatus: z4.enum(["live", "category_cache", "platform_default"])
});
var currencySchema = z4.enum(["CNY", "USD", "RUB"]);
var interpretProductIdeaSchema = z4.object({
  idea: z4.string().trim().min(1).max(2e3),
  analysisMode: analysisModeSchema.default("standalone"),
  debug: z4.boolean().optional().default(false)
});
var suggestSourceTypeSchema = z4.enum([
  "local_dictionary",
  "yandex_suggest",
  "google_suggest",
  "wb_suggest",
  "ai_supplement"
]);
var createResearchRunSchema = z4.object({
  idea: z4.string().trim().min(1).max(2e3),
  entityZh: z4.string().trim().min(1).max(200),
  productTypeZh: z4.string().trim().min(1).max(120).nullable().default(null),
  useCases: z4.array(z4.string().trim().min(1).max(120)).max(5).default([]),
  coreQueries: z4.array(
    z4.object({
      keywordRu: z4.string().trim().min(1).max(200),
      translationZh: z4.string().trim().min(1).max(200).nullable(),
      confidence: z4.number().min(0).max(1)
    })
  ).min(1).max(3),
  analysisMode: analysisModeSchema.default("standalone")
});
var refreshResearchSuggestionsSchema = z4.object({
  force: z4.boolean().default(false)
});
var selectResearchKeywordsSchema = z4.object({
  keywordIds: z4.array(z4.string().uuid()).max(100)
});
var wordstatOpenedSchema = z4.object({
  keywordId: z4.string().uuid()
});
var manualWordstatSchema = z4.object({
  keywordId: z4.string().uuid(),
  frequency: z4.number().finite().nonnegative().nullable().default(null),
  period: z4.string().trim().min(1).max(120).nullable().default(null),
  trend: z4.enum(["rising", "stable", "declining", "seasonal", "insufficient"]).nullable().default(null),
  notes: z4.string().trim().max(2e3).nullable().default(null)
});
var researchCompetitorRefreshSchema = z4.object({
  platforms: z4.array(platformSchema).min(1).max(1).transform((items) => [...new Set(items)])
});
var researchDecisionSchema = z4.object({
  competitorEvidence: z4.array(
    z4.object({
      platform: platformSchema,
      keywordId: z4.string().uuid(),
      itemCount: z4.number().int().nonnegative(),
      relevantCount: z4.number().int().nonnegative(),
      medianPrice: z4.number().finite().nonnegative().nullable(),
      medianReviews: z4.number().finite().nonnegative().nullable(),
      brandCount: z4.number().int().nonnegative().nullable()
    })
  ).max(20).default([])
});
var seoPromptKeywordContextSchema = z4.object({
  keyword: z4.string().trim().min(1).max(200),
  heat: z4.number().finite().nonnegative().nullable().optional(),
  competition: z4.number().finite().nonnegative().nullable().optional(),
  supplyDemandRatio: z4.number().finite().nonnegative().nullable().optional(),
  relevance: z4.number().min(0).max(1).nullable().optional(),
  sourceCount: z4.number().int().nonnegative().nullable().optional()
});
var seoPlatformSchema = z4.enum(["wb", "ozon"]);
var seoPromptTemplateVersionSchema = z4.enum(["wb-seo-v1", "ozon-seo-v1"]);
var extractSeoProductRequestSchema = z4.object({
  rawProductText: z4.string().trim().min(1, "\u8BF7\u7C98\u8D34\u5546\u54C1\u8D44\u6599").max(1e5),
  platform: seoPlatformSchema.nullable().default(null)
});
var seoPromptTextArraySchema = z4.array(z4.string().trim().min(1).max(500)).max(50).default([]);
var seoPromptProductSchema = z4.object({
  productNameCn: z4.string().trim().max(500),
  productTypeCn: z4.string().trim().max(500),
  coreFunctions: seoPromptTextArraySchema,
  features: seoPromptTextArraySchema,
  materials: seoPromptTextArraySchema,
  dimensions: seoPromptTextArraySchema,
  colors: seoPromptTextArraySchema,
  quantity: z4.string().trim().max(500),
  includedItems: seoPromptTextArraySchema,
  useScenarios: seoPromptTextArraySchema,
  targetUsers: seoPromptTextArraySchema,
  sellingPoints: seoPromptTextArraySchema
});
var seoPromptInformationRisksSchema = z4.object({
  uncertain: seoPromptTextArraySchema,
  conflicting: seoPromptTextArraySchema,
  prohibitedClaims: seoPromptTextArraySchema
});
var seoProductEvidenceSchema = z4.object({
  field: z4.string().trim().min(1).max(200),
  value: z4.string().trim().min(1).max(500),
  sourceText: z4.string().trim().min(1).max(1e3)
});
var seoProductExtractionResultSchema = z4.object({
  product: seoPromptProductSchema,
  informationRisks: seoPromptInformationRisksSchema,
  evidence: z4.array(seoProductEvidenceSchema).max(100).default([]),
  multipleProductsDetected: z4.boolean().default(false)
});
var generateSeoPromptRequestSchema = z4.object({
  confirmedProduct: seoPromptProductSchema,
  informationRisks: seoPromptInformationRisksSchema,
  platform: seoPlatformSchema.default("wb"),
  analysisContext: z4.object({
    keywords: z4.array(seoPromptKeywordContextSchema).max(200).default([])
  }).default({ keywords: [] })
});
var seoPromptGenerationResultSchema = z4.object({
  platform: seoPlatformSchema,
  promptTemplateVersion: seoPromptTemplateVersionSchema,
  product: seoPromptProductSchema,
  informationRisks: seoPromptInformationRisksSchema,
  keywords: z4.object({
    primaryKeyword: z4.string().trim().max(200),
    primaryKeywordTranslationZh: z4.string().trim().max(300).optional(),
    coreKeywords: z4.array(
      z4.object({
        keyword: z4.string().trim().min(1).max(200),
        translationZh: z4.string().trim().max(300).optional(),
        heat: z4.number().finite().nonnegative().nullable().optional(),
        heatSupported: z4.boolean().optional(),
        reason: z4.string().trim().min(1).max(1e3)
      })
    ).max(5).default([]),
    longTailKeywords: z4.array(
      z4.object({
        keyword: z4.string().trim().min(1).max(300),
        translationZh: z4.string().trim().max(300).optional(),
        sourceType: z4.enum(["analysis_keyword", "web_verified_synonym", "attribute_combination"]),
        basedOn: z4.array(z4.string().trim().min(1).max(500)).min(1).max(50),
        reason: z4.string().trim().min(1).max(1e3),
        heat: z4.number().finite().nonnegative().nullable(),
        heatSupported: z4.boolean(),
        confidence: z4.number().min(0).max(1)
      })
    ).max(10).default([]),
    optionalKeywords: z4.array(
      z4.object({
        keyword: z4.string().trim().min(1).max(300),
        translationZh: z4.string().trim().max(300).optional(),
        heat: z4.number().finite().nonnegative().nullable().optional(),
        heatSupported: z4.boolean().optional(),
        reason: z4.string().trim().min(1).max(1e3)
      })
    ).max(5).default([]),
    rejectedKeywords: z4.array(
      z4.object({
        keyword: z4.string().trim().min(1).max(300),
        translationZh: z4.string().trim().max(300).optional(),
        reason: z4.string().trim().min(1).max(1e3)
      })
    ).max(10).default([])
  }),
  gptPrompt: z4.string().trim().min(1).max(5e4),
  webSearch: z4.object({
    used: z4.boolean(),
    resultBlockCount: z4.number().int().nonnegative()
  })
});
var moneySchema = z4.object({
  amount: z4.number().finite().nonnegative(),
  currency: currencySchema
});
var nullableText = (maximum) => z4.string().trim().min(1).max(maximum).nullable().default(null);
var analysisLogisticsInputSchema = z4.object({
  volumeDivisor: z4.number().positive().default(5e3),
  ratePerKg: moneySchema.nullable().default(null),
  firstLeg: moneySchema.nullable().default(null),
  tailLeg: moneySchema.nullable().default(null),
  handling: moneySchema.nullable().default(null),
  manualOverride: moneySchema.nullable().default(null)
});
var analysisTemporaryOverridesSchema = z4.object({
  commissionRate: z4.number().min(0).max(1).optional(),
  serviceFeeRate: z4.number().min(0).max(1).optional(),
  advertisingRate: z4.number().min(0).max(1).optional(),
  returnLossRate: z4.number().min(0).max(1).optional(),
  taxRate: z4.number().min(0).max(1).optional(),
  exchangeMarkupRate: z4.number().min(0).max(1).optional(),
  targetMarginRate: z4.number().min(0).max(0.95).optional(),
  fixedCosts: z4.array(moneySchema).max(20).optional()
});
var analysisPlatformInputSchema = z4.object({
  sellingPrice: moneySchema.nullable().default(null),
  costProfileId: z4.string().uuid().nullable().default(null),
  temporaryOverrides: analysisTemporaryOverridesSchema.default({}),
  logistics: analysisLogisticsInputSchema.default({
    volumeDivisor: 5e3,
    ratePerKg: null,
    firstLeg: null,
    tailLeg: null,
    handling: null,
    manualOverride: null
  })
});
var createProductAnalysisSchema = z4.object({
  mode: z4.enum(["simple", "full"]).default("simple"),
  analysisMode: analysisModeSchema.default("standalone"),
  productName: z4.string().trim().min(1).max(200),
  idea: nullableText(2e3),
  sourceKeyword: nullableText(200),
  productLink: z4.string().url().nullable().default(null),
  sku: nullableText(200),
  material: nullableText(500),
  dimensions: nullableText(500),
  structure: nullableText(1e3),
  sourcePlatform: platformSchema.nullable().default(null),
  sourceKeywordId: z4.string().uuid().nullable().default(null),
  platforms: z4.array(platformSchema).min(1).max(1).transform((items) => [...new Set(items)]),
  resolvedProductId: z4.string().uuid().nullable().default(null),
  forceNewProduct: z4.boolean().default(false),
  netWeightKg: z4.number().finite().nonnegative().nullable().default(null),
  packagedWeightKg: z4.number().finite().nonnegative().nullable().default(null),
  lengthCm: z4.number().finite().nonnegative().nullable().default(null),
  widthCm: z4.number().finite().nonnegative().nullable().default(null),
  heightCm: z4.number().finite().nonnegative().nullable().default(null),
  quantity: z4.number().int().positive().max(1e5).default(1),
  purchasePrice: moneySchema.nullable().default(null),
  outputCurrency: currencySchema.default("CNY"),
  platformInputs: z4.object({
    wb: analysisPlatformInputSchema.optional()
  }).default({})
});
var createCostProfileSchema = z4.object({
  platform: platformSchema,
  fulfillmentMethod: z4.string().trim().min(1).max(100),
  name: z4.string().trim().min(1).max(120),
  defaultCurrency: currencySchema,
  config: z4.object({
    commissionRate: z4.number().min(0).max(1).default(0),
    serviceFeeRate: z4.number().min(0).max(1).default(0),
    advertisingRate: z4.number().min(0).max(1).default(0),
    returnLossRate: z4.number().min(0).max(1).default(0),
    taxRate: z4.number().min(0).max(1).default(0),
    exchangeMarkupRate: z4.number().min(0).max(1).default(0),
    targetMarginRate: z4.number().min(0).max(0.95).default(0.25),
    fixedCosts: z4.array(moneySchema).max(20).default([]),
    logistics: analysisLogisticsInputSchema.default({
      volumeDivisor: 5e3,
      ratePerKg: null,
      firstLeg: null,
      tailLeg: null,
      handling: null,
      manualOverride: null
    })
  })
});
var createExchangeRateSchema = z4.object({
  baseCurrency: currencySchema,
  quoteCurrency: currencySchema,
  rate: z4.number().finite().positive(),
  source: z4.string().trim().min(1).max(200),
  rateDate: z4.string().datetime({ offset: true }),
  status: z4.enum(["official", "settlement", "manual", "stale_fallback"]).default("manual")
}).refine((value) => value.baseCurrency !== value.quoteCurrency, {
  message: "Base and quote currencies must differ.",
  path: ["quoteCurrency"]
});
var restoreProductAnalysisSchema = z4.object({
  expectedVersion: z4.number().int().positive()
});
var requestExternalDataSchema = z4.object({
  sources: z4.array(z4.enum(["public_web", "regulation", "platform_external"])).min(1).max(4).transform((items) => [...new Set(items)]),
  platforms: z4.array(platformSchema).max(1).default([])
});
var createBackupSchema = z4.object({
  protected: z4.boolean().default(false),
  simulateFailure: z4.boolean().default(false)
});
var restoreBackupSchema = z4.object({
  confirmation: z4.string().trim().min(1).max(120),
  reason: z4.string().trim().min(8).max(1e3),
  expectedVersion: z4.number().int().positive()
});
var createTestSnapshotSchema = z4.object({
  platforms: z4.array(platformSchema).min(1).max(1).transform((items) => [...new Set(items)]),
  sampleSize: z4.number().int().min(1).max(1e5),
  confirmOverwrite: z4.literal(true)
});
var releaseCandidateSchema = z4.object({
  source: z4.enum(["github_release", "simulation"]),
  tag: z4.string().trim().min(1).max(100),
  version: z4.string().trim().min(1).max(100),
  releaseNotes: z4.string().trim().min(1).max(2e4),
  migrationSummary: z4.string().trim().max(1e4),
  assetName: z4.string().trim().min(1).max(255),
  assetSha256: z4.string().regex(/^[a-fA-F0-9]{64}$/),
  assetUrl: z4.string().url()
});
var simulateReleaseInstallSchema = z4.object({
  releaseId: z4.string().uuid(),
  confirmation: z4.string().trim().min(1).max(120),
  failAt: z4.enum([
    "confirm",
    "maintenance",
    "drain_jobs",
    "backup",
    "fetch_asset",
    "verify_asset",
    "migration_check",
    "switch_version",
    "migrate",
    "health_check",
    "complete"
  ]).optional()
});
var runCleanupSchema = z4.object({
  confirmation: z4.literal("RUN CLEANUP")
});
var jobStatusSchema = z4.enum([
  "queued",
  "running",
  "paused",
  "cancelling",
  "cancelled",
  "succeeded",
  "failed"
]);
export {
  analysisLogisticsInputSchema,
  analysisModeSchema,
  analysisPlatformInputSchema,
  analysisTemporaryOverridesSchema,
  batchUpdateDecisionRequestSchema,
  competitorRefreshSchema,
  competitorSampleUpdateSchema,
  createBackupSchema,
  createCostProfileSchema,
  createDictionaryEntrySchema,
  createExchangeRateSchema,
  createProductAnalysisSchema,
  createPromptVersionSchema,
  createResearchRunSchema,
  createTestSnapshotSchema,
  createUploadRequestSchema,
  createUserRequestSchema,
  currencySchema,
  erpAccessSchema,
  erpContentSchema,
  erpCreateProductSchema,
  erpCreateStoreSchema,
  erpDailyTaskSchema,
  erpGenerateContentSchema,
  erpMarketplaceSchema,
  erpMemberSchema,
  erpPricingSchema,
  erpProductBatchSchema,
  erpProductQuerySchema,
  erpProductStatusSchema,
  erpSaveWorkflowSchema,
  erpSourceRowSchema,
  erpUpdateProductSchema,
  erpUpdateStoreSchema,
  erpVariantSchema,
  erpWorkflowSchema,
  extractSeoProductRequestSchema,
  generateSeoPromptRequestSchema,
  interpretProductIdeaSchema,
  jobStatusSchema,
  keywordProcessingActionSchema,
  keywordRelationsSchema,
  keywordSearchRequestSchema,
  loginRequestSchema,
  manualWordstatSchema,
  moneySchema,
  platformSchema,
  platformsForAnalysisMode,
  preferenceValueSchema,
  productCardDraftSchema,
  productMaterialSchema,
  publicUserSchema,
  refreshResearchSuggestionsSchema,
  registerRequestSchema,
  releaseCandidateSchema,
  requestExternalDataSchema,
  researchCompetitorRefreshSchema,
  researchDecisionSchema,
  resetPasswordRequestSchema,
  restoreBackupSchema,
  restoreProductAnalysisSchema,
  runCleanupSchema,
  saveProductCardSchema,
  selectResearchKeywordsSchema,
  seoPlatformSchema,
  seoProductExtractionResultSchema,
  seoPromptGenerationResultSchema,
  seoPromptInformationRisksSchema,
  seoPromptKeywordContextSchema,
  seoPromptProductSchema,
  seoPromptTemplateVersionSchema,
  simulateReleaseInstallSchema,
  suggestSourceTypeSchema,
  updateDecisionRequestSchema,
  updateExtractedAttributeSchema,
  updateUserRequestSchema,
  userDecisionSchema,
  userRoleSchema,
  wordstatOpenedSchema
};
